import { faX } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';

export enum TypeOfView {
  NEW_TAB,
  RENAME_TAB,
  DUPLICATE_TAB,
  DELETE_TAB,
}

const TITLES = {
  [TypeOfView.NEW_TAB]: 'New tab',
  [TypeOfView.RENAME_TAB]: 'Rename tab',
  [TypeOfView.DUPLICATE_TAB]: 'Duplicate tab',
  [TypeOfView.DELETE_TAB]: 'Are you sure you want to delete this tab?',
};

interface IHandleTabModalProps {
  open: boolean;
  handleClose: () => void;
  saveTab: (tab: string, tokenPair: string) => void;
  deleteTab: () => void;
  duplicateTab: (tab: string) => void;
  renameTab: (newName: string) => void;
  typeOfView: TypeOfView;
}

import './styles.scss';
import Input from '../../../../components/input/input';
import { OTCTraderContext } from '../../../../providers/otc-trader/otc-trader-provider';
import { OtcTraderSelect } from '../../../../components/v2/widgets/volatility-surface-marking/volatility-suface-marking-sidebar/otc-trader-select';
import { VolatilitySufaceMarkingSidebarSelect } from '../volatility-suface-marking-sidebar-select';

const HandleTabModal = (props: IHandleTabModalProps) => {
  const { open, typeOfView, handleClose, saveTab, renameTab, deleteTab, duplicateTab } = props;

  const [tabName, setTabName] = useState<string | undefined>('');
  const [tokenPair, setTokenPair] = useState<string | undefined>('');
  const { marketPairTokens, tabs } = useContext(OTCTraderContext);

  const isRenameOrDuplicate = [TypeOfView.RENAME_TAB, TypeOfView.DUPLICATE_TAB].includes(
    typeOfView,
  );

  const isNewtab = TypeOfView.NEW_TAB === typeOfView;

  const hasTabWithSameName = (name?: string) => {
    return !!tabs.find((tab) => tab.label === name);
  };

  const isValidName = useMemo(() => !hasTabWithSameName(tabName), [tabs, tabName]);

  const isValidOptions =
    (isNewtab && !!tokenPair?.length && !!tabName?.length) ||
    (isRenameOrDuplicate && !!tabName?.length && isValidName);

  const handleSave = () => {
    handleClose();
    setTabName('');
    switch (typeOfView) {
      case TypeOfView.DELETE_TAB:
        deleteTab();
        break;
      case TypeOfView.DUPLICATE_TAB:
        duplicateTab(tabName!);
        break;
      case TypeOfView.RENAME_TAB:
        renameTab(tabName!);
        break;
      case TypeOfView.NEW_TAB:
        saveTab(tabName!, tokenPair!);
        break;
    }
  };

  const renderOptions = useMemo(() => {
    const renderNameInput = () => {
      const alreadyExistsError = 'A tab with this name already exists';

      return (
        <div className="item">
          <span className="label">Tab name</span>
          <Input
            placeholder="Tab name"
            value={tabName}
            onChange={(e: any) => {
              setTabName(e.target.value);
            }}
            errors={!isValidName ? alreadyExistsError : undefined}
          />
        </div>
      );
    };

    const renderTokenPairSelect = () => {
      return (
        <div className="item">
          <span className="label">Token Pair</span>
          <VolatilitySufaceMarkingSidebarSelect>
            <OtcTraderSelect
              values={marketPairTokens as any}
              defaultValue={{
                name: tokenPair || 'Token Pair',
                value: tokenPair || '',
              }}
              onChange={({ value }) => setTokenPair(value)}
              isSearchField
              className="select-input"
            />
          </VolatilitySufaceMarkingSidebarSelect>
        </div>
      );
    };

    switch (typeOfView) {
      case TypeOfView.DELETE_TAB:
        return null;
      case TypeOfView.DUPLICATE_TAB:
      case TypeOfView.RENAME_TAB:
        return <div className="items">{renderNameInput()}</div>;

      case TypeOfView.NEW_TAB:
        return (
          <div className="items">
            {renderTokenPairSelect()}
            {renderNameInput()}
          </div>
        );
      default:
        return null;
    }
  }, [setTabName, tabs, tabName, marketPairTokens, typeOfView, tokenPair]);

  const renderButtons = () => {
    if (typeOfView === TypeOfView.DELETE_TAB) {
      return (
        <div className="buttons">
          <Button className="cancel-button" onClick={handleClose}>
            Cancel
          </Button>
          <Button className="delete-button" onClick={handleSave}>
            Delete anyway
          </Button>
        </div>
      );
    }

    return (
      <div className="buttons">
        <Button className="add-button" onClick={handleSave} disabled={!isValidOptions}>
          Save
        </Button>
      </div>
    );
  };

  useEffect(() => {
    if (tokenPair && tokenPair.length && tokenPair.split('_')?.length === 2) {
      let baseName = `${tokenPair.split('_')[0]} Pricer`;

      let count = 1;

      while (hasTabWithSameName(baseName)) {
        const newName = baseName + ' ' + count;

        if (!hasTabWithSameName(newName)) {
          baseName = newName;
        } else {
          count++;
        }
      }
      setTabName(baseName);
    }
  }, [tokenPair, setTabName]);

  useEffect(() => {
    setTokenPair('');
    setTabName('');
  }, [typeOfView, open]);

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="handle-tab-modal-container">
        <div className="handle-tab-modal-content">
          <div className="header">
            <h1>{TITLES[typeOfView]}</h1>
            <Button className="close-button" onClick={handleClose}>
              <FontAwesomeIcon icon={faX} />
            </Button>
          </div>
          {renderOptions}
          {renderButtons()}
        </div>
      </div>
    </Modal>
  );
};

export default HandleTabModal;
